import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { agentApi } from '../../services/api';
import { toast } from 'react-toastify';

interface BulkSyncDialogProps {
  open: boolean;
  onClose: () => void;
  agentType?: 'AI_SYSTEM_BUILDER' | 'ASSISTANT';
}

export const BulkSyncDialog: React.FC<BulkSyncDialogProps> = ({
  open,
  onClose,
  agentType = 'AI_SYSTEM_BUILDER',
}) => {
  const queryClient = useQueryClient();
  const [useAsync, setUseAsync] = useState(true);

  const syncMutation = useMutation({
    mutationFn: () =>
      agentApi.syncAllTemplates({
        agent_type: agentType,
        async: useAsync,
      }),
    onSuccess: (response) => {
      if (response.status === 'queued') {
        toast.success(
          `Bulk sync queued! Task ID: ${response.task_id}`,
          { autoClose: 5000 }
        );
      } else if (response.status === 'completed') {
        toast.success(
          `Successfully synced ${response.synced_count} agent(s)`
        );
      }

      // Invalidate queries to refresh agent list
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error || 'Failed to initiate bulk sync'
      );
    },
  });

  const handleSync = () => {
    syncMutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SyncIcon color="primary" />
          <Typography variant="h6">Bulk Sync All Agents</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <Alert severity="info">
            This will sync all user instances of <strong>{agentType}</strong>{' '}
            agents from the latest template version.
          </Alert>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Agent Type:
            </Typography>
            <Chip label={agentType} color="primary" />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={useAsync}
                onChange={(e) => setUseAsync(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  Run asynchronously (Celery)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {useAsync
                    ? 'Task will run in background. You can continue working.'
                    : 'Task will run synchronously. May take longer for many users.'}
                </Typography>
              </Box>
            }
          />

          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Important:</strong> This operation will:
            </Typography>
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Update all non-customized agents to the latest template</li>
              <li>Preserve user customizations</li>
              <li>Add/remove tools based on template changes</li>
            </ul>
          </Alert>

          {syncMutation.isError && (
            <Alert severity="error">
              {syncMutation.error?.response?.data?.error ||
                'Failed to sync agents'}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={syncMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSync}
          disabled={syncMutation.isPending}
          startIcon={
            syncMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              <SyncIcon />
            )
          }
        >
          {syncMutation.isPending ? 'Syncing...' : 'Sync All'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
